import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type Address from '../../types/Address';
import { InfoText, Redeem } from '../../types/Redeem';
import { RootState } from '../store';

type RedeemInitialState = Omit<Redeem, 'infoText'> & { infoText?: InfoText };

const initialState: RedeemInitialState = {
  code: '',
  customerId: '',
  campaignId: '',
  infoText: undefined,
  consent: undefined,
  logoUrl: undefined,
  shippingCountries: undefined,
  shippingAddress: undefined,
};

/* Reducers */
const redeemSlice = createSlice({
  name: 'redeem',
  initialState,
  reducers: {
    /**
     * Stores the redeem auth payload after the validate-redeem-code call
     * resolves. Code validity is enforced server-side at submit time, so
     * we don't track a client-side freshness window — if the code has
     * expired between validation and submission, the API will return the
     * appropriate failure reason and the user will be redirected then.
     */
    authenticated(state, action: PayloadAction<Redeem>) {
      state.code = action.payload.code;
      state.customerId = action.payload.customerId;
      state.campaignId = action.payload.campaignId;
      state.infoText = action.payload.infoText;
      // `consent` is assigned unconditionally even though it's optional:
      // it describes the code being authenticated, so an absent value has
      // to clear any checkbox left over from a previous code rather than
      // preserve it.
      state.consent = action.payload.consent;
      // Same for `logoUrl`, so a previous code's logo doesn't carry over.
      state.logoUrl = action.payload.logoUrl;
      // `shippingCountries` and `shippingAddress` remain optional — only
      // assign when the payload supplies them so we don't overwrite with
      // `undefined` and lose previously-stored data on a no-op re-dispatch.
      if (action.payload.shippingCountries) {
        state.shippingCountries = action.payload.shippingCountries;
      }
      if (action.payload.shippingAddress) {
        state.shippingAddress = action.payload.shippingAddress;
      }
    },
    /**
     * Captures the address the user confirmed on the address-collection
     * step (after the verify-address modal closes). The summary page
     * reads this back to render the order summary.
     */
    shippingAddressUpdated(state, action: PayloadAction<Address>) {
      state.shippingAddress = action.payload;
    },
    /**
     * Resets the slice to its initial empty state. Dispatch on success,
     * an unrecoverable failure, or an explicit "start over" so we don't
     * leave stale auth around.
     */
    cleared() {
      return initialState;
    },
  },
});

/* Actions */
export const { authenticated, cleared, shippingAddressUpdated } =
  redeemSlice.actions;

/* Selectors */
export const selectRedeem = (state: RootState) => state.redeem;

/** True when a code has been captured. */
export const selectIsRedeemAuthenticated = (state: RootState) =>
  Boolean(state.redeem.code);

export default redeemSlice.reducer;
